import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Shows a spinner and disables the button while a task is in flight. */
    busy?: boolean;
    /** Optional leading icon (e.g. a 14-16px Lucide glyph). */
    icon?: ReactNode;
    /** Optional trailing icon — for directional buttons ("Advance →"). Hidden while busy. */
    iconRight?: ReactNode;
    children?: ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
    primary:
        'bg-[rgb(var(--color-accent))] text-white border border-transparent ' +
        'hover:bg-[rgb(var(--color-accent)/0.9)]',
    secondary:
        'bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-primary))] ' +
        'border border-[rgb(var(--color-divider))] ' +
        'hover:bg-[rgb(var(--color-surface-secondary))] hover:border-[rgb(var(--color-text-primary)/0.2)]',
    ghost:
        'bg-transparent text-[rgb(var(--color-text-primary))] border border-transparent ' +
        'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
    danger:
        'bg-[rgb(var(--color-rag-red))] text-white border border-transparent ' +
        'hover:bg-[rgb(var(--color-rag-red)/0.9)]',
};

// xs is 22px, sm is 24px and md is 28px. A coarse pointer raises each size to 32px.
const SIZE: Record<ButtonSize, string> = {
    xs: 'h-[22px] px-1.5 gap-1 text-caption pointer-coarse:h-8',
    sm: 'h-6 px-2 gap-1 text-annotation pointer-coarse:h-8',
    md: 'h-7 px-2.5 gap-1.5 text-annotation pointer-coarse:h-8',
};

// A button with an icon and no label is square. xs and sm give 24x24.
const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
    xs: 'h-6 w-6 px-0 pointer-coarse:h-8 pointer-coarse:w-8',
    sm: 'h-6 w-6 px-0 pointer-coarse:h-8 pointer-coarse:w-8',
    md: 'h-7 w-7 px-0 pointer-coarse:h-8 pointer-coarse:w-8',
};

// Single button primitive for the kit. Replaces every hand-rolled
// `<button className="bg-[rgb(var(--color-accent))]...">` and the
// copy-pasted ExportButtons (see ExportButton.tsx, which builds on this).
//
// Side-by-side uniformity rule: `Button` and `Select` share one height per
// size (`md` = 28px, `sm` = 24px) and the same radius/border, so they line up
// perfectly when placed in a row. Keep every control in a horizontal group on
// ONE size — never mix `sm` and `md` side by side. Default to `md` for
// page/toolbar/dialog actions; reserve `sm` for dense inline rows (table-row
// actions, compact filter bars) where the whole row is `sm`.
/**
 * The classes of a kit button. A route out of a page is a link and not a
 * button, so a `<Link>` that must look like a button reads its classes here.
 */
export function buttonClassName({
    variant = 'secondary',
    size = 'md',
    iconOnly = false,
}: {
    variant?: ButtonVariant;
    size?: ButtonSize;
    iconOnly?: boolean;
} = {}): string {
    return cn(
        'inline-flex items-center justify-center rounded-button font-medium',
        'transition-colors duration-card-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.4)]',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANT[variant],
        iconOnly ? ICON_ONLY_SIZE[size] : SIZE[size],
    );
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'secondary',
        size = 'md',
        busy = false,
        icon,
        iconRight,
        disabled,
        type = 'button',
        className,
        children,
        ...rest
    },
    ref,
) {
    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled || busy}
            aria-busy={busy || undefined}
            className={cn(
                buttonClassName({ variant, size, iconOnly: Boolean(icon) && children == null }),
                className,
            )}
            {...rest}
        >
            {busy ? (
                <Loader2
                    aria-hidden="true"
                    className={cn('animate-spin', size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5')}
                />
            ) : (
                icon
            )}
            {children}
            {!busy && iconRight}
        </button>
    );
});

export default Button;
